import { useCallback, useEffect, useRef } from 'react'
import type { Location, NavigateFunction } from 'react-router-dom'
import type { BootstrapPayload, LoginCredentials, LoginResponse } from '../../types/auth'
import type { StatusMessage } from '../../types/status'
import { bootstrap, login, logout } from '../../api/authApi'
import { registerApiErrorHandler } from '../../api/httpClient'
import { resolveApiErrorMessage } from './shared'
import { validateBootstrapForm } from '../../utils/authFormValidation'
import { buildReturnPathState, resolvePostLoginPath } from '../../utils/returnPath'

interface UseAuthSessionActionsParams {
  apiBaseUrl: string
  navigate: NavigateFunction
  location: Location
  clearAuthState: () => void
  applyAuthUserData: (userData: Partial<LoginResponse>, fallbackUserName?: string) => {
    userName: string
    userEmail: string
    mustChangePassword: boolean
  }
  resetProfileForm: (userName?: string, userEmail?: string) => void
  setSetupRequired: (value: boolean) => void
  setSignedOutByUser: (value: boolean) => void
  setAuthStatus: (status: StatusMessage | null) => void
  setProfileStatus: (status: StatusMessage | null) => void
}

export interface UseAuthSessionActionsResult {
  handleLogin: (credentials: LoginCredentials) => Promise<void>
  handleBootstrap: (payload: BootstrapPayload) => Promise<void>
  handleLogout: () => Promise<void>
  handleUnauthorizedSession: () => void
}

export function useAuthSessionActions({
  apiBaseUrl,
  navigate,
  location,
  clearAuthState,
  applyAuthUserData,
  resetProfileForm,
  setSetupRequired,
  setSignedOutByUser,
  setAuthStatus,
  setProfileStatus
}: UseAuthSessionActionsParams): UseAuthSessionActionsResult {
  const currentLocationRef = useRef(location)

  useEffect(() => {
    currentLocationRef.current = location
  }, [location])

  const handleLogin = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    setAuthStatus({ kind: 'loading', message: 'Выполняется вход...' })

    try {
      const data = await login(apiBaseUrl, credentials)
      const { userName, userEmail, mustChangePassword } = applyAuthUserData(data, credentials.username)

      setSetupRequired(false)
      setAuthStatus({ kind: 'success', message: 'Вход выполнен.' })

      if (mustChangePassword) {
        resetProfileForm(userName, userEmail)
        setProfileStatus({
          kind: 'error',
          message: 'Это первый вход. Необходимо сменить пароль.'
        })
      }

      navigate(resolvePostLoginPath(currentLocationRef.current.state, mustChangePassword), { replace: true })
    } catch (error) {
      setAuthStatus({ kind: 'error', message: resolveApiErrorMessage(error, apiBaseUrl, 'Ошибка входа.') })
    }
  }, [apiBaseUrl, applyAuthUserData, navigate, resetProfileForm, setAuthStatus, setProfileStatus, setSetupRequired])

  const handleBootstrap = useCallback(async (form: BootstrapPayload): Promise<void> => {
    const validationError = validateBootstrapForm(form)
    if (validationError) {
      setAuthStatus({ kind: 'error', message: validationError })
      return
    }

    setAuthStatus({ kind: 'loading', message: 'Создание первого пользователя...' })

    try {
      await bootstrap(apiBaseUrl, form)

      setSetupRequired(false)
      await handleLogin({ username: form.username, password: form.password })
    } catch (error) {
      setAuthStatus({ kind: 'error', message: resolveApiErrorMessage(error, apiBaseUrl, 'Ошибка инициализации.') })
    }
  }, [apiBaseUrl, handleLogin, setAuthStatus, setSetupRequired])

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await logout(apiBaseUrl)
    } finally {
      clearAuthState()
      setSignedOutByUser(true)
      setAuthStatus(null)
      navigate('/login', { replace: true })
    }
  }, [apiBaseUrl, clearAuthState, navigate, setAuthStatus, setSignedOutByUser])

  const handleUnauthorizedSession = useCallback((): void => {
    clearAuthState()
    setAuthStatus({ kind: 'error', message: 'Сессия истекла. Выполните вход снова.' })
    navigate('/login', { replace: true, state: buildReturnPathState(currentLocationRef.current) })
  }, [clearAuthState, navigate, setAuthStatus])

  useEffect(() => registerApiErrorHandler((error) => {
    if (error.status !== 401) {
      return false
    }

    handleUnauthorizedSession()
    return true
  }), [handleUnauthorizedSession])

  return {
    handleLogin,
    handleBootstrap,
    handleLogout,
    handleUnauthorizedSession
  }
}
